import { Component, computed, DestroyRef, inject, input } from '@angular/core';

import { MedicalPhotosEndpoints } from '@pims-endpoints';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY } from 'rxjs';
import { VisitDetailPhotos } from '@types-lib';
import { TaskPhotosComponent } from '../../../visitid/tabs/photos/identification/task-photos/task-photos.component';
import { ENVIRONMENT, PIMSEnvironment } from 'environments';

interface PhotoDocuments {
  creditsLeft?: number;
  files?: VisitDetailPhotos.Photo[];
}

@Component({
  selector: 'medication-photo',
  standalone: true,
  imports: [TaskPhotosComponent],
  templateUrl: './medication-photo.component.html',
  styleUrls: ['./medication-photo.component.scss'],
})
export class MedicationPhotoComponent {
  readonly visit = input<any>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly endpoints = inject(MedicalPhotosEndpoints);
  private readonly env = inject<PIMSEnvironment>(ENVIRONMENT);

  readonly _isLoading = computed(() => this.documentsResource.isLoading());
  readonly photosDocuments = computed<PhotoDocuments>(() => {
    const value = this.documentsResource.value();
    const creditsLeft = value?.credits_left;
    const files = value?.files;
    const newFiles = files?.map((file, idx) => {
      const photoDocument: VisitDetailPhotos.PhotoFile = {
        deleted: null,
        exam_photo_id: idx,
        filename: file.filename,
        id: idx + Math.round(Math.random() * 1000),
        id_type: null,
        notes: null,
        rejection_note: '',
        reviewed: '',
        reviewed_by: 0,
        status: VisitDetailPhotos.PhotoStatuses.APPROVED,
        image_uri: file.link,
        timestamp: file.created,
      };
      const doc: VisitDetailPhotos.Photo = {
        id: idx + Math.round(Math.random() * 1000),
        text: 'Medication',
        camera_side: 'front',
        type: 'Medication',
        optional: false,
        more: {
          title: 'Medication',
          text: '',
        },
        file: photoDocument,
        rejected: false,
        approved: false,
        deleted: false,
        notes: '',
        image_uri: file.filename,
      };
      return doc;
    });
    return {
      creditsLeft,
      files: newFiles,
    };
  });

  documentsResource = rxResource({
    params: () => {
      const raw = this.visit()?.user.id;
      if (raw == null) return null;
      const id = typeof raw === 'string' ? Number.parseInt(raw, 10) : raw;
      return Number.isFinite(id) ? (id as number) : null;
    },
    stream: ({ params }) => {
      if (params == null) return EMPTY;

      return this.endpoints.get(params).pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(err => {
          console.error(`Error fetching medical documents: ${err}`);
          throw err;
        }),
      );
    },
  });
}
